import React, { useState, useEffect } from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import { TextChat } from './TextChat';
import { ChatControlWrapper } from '../../shared/ChatControls';
import { Info, Image, ArrowLeft, Images, Menu } from 'lucide-react';
import { useActiveImage, useProfileImage, useCharacterData, useChatStatus, useGlobalChatState, useGlobalLiveKitRoomState } from '../../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../../data/liveMessageData/liveMessage';
import { ChatStatus } from '../../../data/globalChatState';
import { AdvancedImage } from '@cloudinary/react';
import { useTranslations } from 'next-intl';

import ConnectionIssuePopup from '@/app/popup/ConnectionIssuePopup';
import { CharacterSidePanel } from './CharacterSidePanel';
import '@/app/ui/scrollbar.css';
import AudioUnlocker from '@/app/components/AudioUnlocker';

interface ChatLayoutProps {
  children: React.ReactNode;
}

export function ChatLayout({ children }: ChatLayoutProps) {
  const t = useTranslations('chat');
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  // Get active image from global state
  const { loadingStatus: activeImageStatus, imageData: activeImageData } = useActiveImage();

  // Toggle function for side panel
  const toggleSidePanel = () => {
    setIsSidePanelOpen(!isSidePanelOpen);
  };

  return (
    <div className={`flex flex-row justify-center w-full h-full relative`}>
      {/* Blurred background image - adapts to container */}
      {activeImageStatus === LiveImageStatus.LOADED && activeImageData && (
        <div className="absolute inset-0 w-full h-full overflow-hidden" style={{ zIndex: 0 }}>
          <AdvancedImage
            cldImg={activeImageData}
            alt="Background"
            className="w-full h-full object-cover"
            style={{
              filter: 'blur(60px) brightness(0.85)',
            }}
          />
        </div>
      )}
      
      {/* Content overlay */}
      <div className="w-full relative z-10">
        <main className="flex flex-col w-full h-full items-center justify-center p-4">
          <div 
            className="flex flex-row items-center justify-center w-full h-full gap-0 relative max-w-[1200px]" 
            style={{ backgroundColor: 'transparent' }}
          >
            {/* Pass toggle function only to UnifiedChatPanel components */}
            {React.Children.map(children, child => {
              if (React.isValidElement(child) && child.type === UnifiedChatPanel) {
                return React.cloneElement(child, { 
                  onToggleSidePanel: toggleSidePanel,
                } as any);
              }
              return child;
            })}
          </div>
        </main>
      </div>

      {/* Character Side Panel */}
      <CharacterSidePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </div>
  );
}

// Unified Chat Panel Components
interface UnifiedChatPanelProps {
  
  // Callbacks
  onLeaveChat?: () => void;
  onStartChat?: () => void;
  onToggleSidePanel?: () => void;
}

interface ProfileHeaderProps {
  onToggleInfoPanel?: () => void;
  onToggleSidePanel?: () => void;
  onToggleGallery?: () => void;
  onBack?: () => void;
}

function ProfileHeader({ onToggleInfoPanel, onToggleSidePanel, onToggleGallery, onBack }: ProfileHeaderProps) {
  const t = useTranslations('chat');
  // Get data from global state
  const { character: character } = useCharacterData();
  const { loadingStatus: profileImageStatus, imageData: profileImageData } = useProfileImage();
  
  const presignedUrl = profileImageStatus === LiveImageStatus.LOADED ? profileImageData?.src : undefined;

  // Don't render if no avatar data
  if (!character) {
    return (
      <div className="flex items-center gap-3 p-4 flex-shrink-0">
        <div className="text-white/60 text-sm">{t('loadingCharacter')}</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between pt-4 px-6 pb-1 flex-shrink-0">
      {/* Back button */}
      <div className="flex-shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-2 mr-6 text-white/70 hover:text-white transition-colors text-xs font-medium"
          title="Go back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Centered character name */}
      <div className="flex-1 overflow-hidden">
        <button
          onClick={onToggleSidePanel}
          className="text-lg font-semibold text-white drop-shadow-lg truncate hover:text-white/80 transition-colors w-full text-center block"
          title="View character details"
        >
          {character.metadata.avatar_name || 'Unknown Avatar'}
        </button>
      </div>

      {/* Right side buttons */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Gallery button */}
        <button
          onClick={onToggleGallery}
          className="flex items-center justify-center w-8 h-8 hover:bg-black/20 rounded-full transition-colors"
          title="Image Gallery"
        >
          <Images className="w-4 h-4 text-white/70 hover:text-white" />
        </button>
        {/* View Profile button */}
        <button
          onClick={onToggleSidePanel}
          data-profile-info-button
          className="flex items-center justify-center w-8 h-8 hover:bg-black/20 rounded-full transition-colors"
          title="View character details"
        >
          <Menu className="w-4 h-4 text-white/70 hover:text-white" />
        </button>
      </div>
    </div>
  );
}

interface StatusBannerProps {
  chatState: ChatStatus;
}

function StatusBanner({ chatState }: StatusBannerProps) {
  const t = useTranslations('chat');
  const getStatusContent = () => {
    switch (chatState) {
      case ChatStatus.INFO:
        return {
          text: 'Previous Conversations',
          icon: null
        };
      case ChatStatus.LOADING:
        return {
          text: t('connecting'),
          icon: <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
        };
      case ChatStatus.LIVE:
        return {
          text: 'Live Video Active',
          icon: <div className={`w-2 h-2 rounded-full bg-green-500`} />
        };
      default:
        return {
          text: 'Ready',
          icon: null
        };
    }
  };

  const status = getStatusContent();

  return (
    <div className="px-4 py-2 bg-black/20 border-b border-white/20 flex-shrink-0">
      <div className="flex items-center gap-2">
        {status.icon}
        <p className="text-xs font-medium text-white/70 drop-shadow-md">
          {status.text}
        </p>
      </div>
    </div>
  );
}

interface ChatMessagesContainerProps {
  onLeaveChat?: () => void;
  onStartChat?: () => void;
  isGalleryOpen: boolean;
  onToggleGallery: () => void;
}

function ChatMessagesContainer({
  onLeaveChat,
  onStartChat,
  isGalleryOpen,
  onToggleGallery,
}: ChatMessagesContainerProps) {
  const t = useTranslations('chat');
  const [globalState, globalActions] = useGlobalChatState();
  const { chatStatus, setChatStatus } = useChatStatus();
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  
  // Get character data
  const { character: avatar } = useCharacterData();
  
  const avatarId = avatar?.metadata.avatar_id || '';
  const hasHistory = avatar?.chatHistory?.hasHistory || false;
  
  // Inactivity timeout state
  const [isInactivityTimeoutPopupOpen, setIsInactivityTimeoutPopupOpen] = React.useState(false);
  const [inactivityTimeoutReason, setInactivityTimeoutReason] = React.useState<'server_signal' | 'user_inactivity' | null>(null);
  
  // React to inactivity timeout state changes from global state
  React.useEffect(() => {
    const checkInactivityTimeout = () => {
      const timeoutState = globalState.isInactivityTimeoutPopupOpen;
      if (timeoutState && !isInactivityTimeoutPopupOpen) {
        setIsInactivityTimeoutPopupOpen(true);
        setInactivityTimeoutReason(globalState.inactivityTimeoutReason);
      }
    };
    
    checkInactivityTimeout();
  }, [globalState.isInactivityTimeoutPopupOpen, globalState.inactivityTimeoutReason, isInactivityTimeoutPopupOpen]);
  
  // Handle inactivity timeout popup close
  const handleInactivityTimeoutClose = () => {
    setIsInactivityTimeoutPopupOpen(false);
    setInactivityTimeoutReason(null);
    
    // Also reset the global state
    globalActions.setInactivityTimeoutState({
      isInactivityTimeoutPopupOpen: false,
      inactivityTimeoutReason: null
    });
  };

  // Always show the TextChat component immediately, don't block on history loading
  // Show a small loading indicator inside the chat area if history is still loading
  
  // Don't render if no avatar data
  if (!avatar) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-4">
        <span className="text-white/60 text-sm">{t('loadingCharacter')}</span>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 relative">
      <TextChat
        onLeaveChat={onLeaveChat}
        onStartChat={onStartChat}
        isGalleryOpen={isGalleryOpen}
        onToggleGallery={onToggleGallery}
      />
      
      {/* Inactivity Timeout Popup */}
      <ConnectionIssuePopup
        isOpen={isInactivityTimeoutPopupOpen}
        onClose={handleInactivityTimeoutClose}
        type="inactivity_timeout"
        inactivityReason={inactivityTimeoutReason}
      />
    </div>
  );
}

export function UnifiedChatPanel({
  onLeaveChat,
  onStartChat,
  onToggleSidePanel,
}: UnifiedChatPanelProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  // Get data from global state
  const [globalState, globalActions] = useGlobalChatState();
  const { chatStatus, setChatStatus } = useChatStatus();
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const { character: avatar } = globalState;

  const avatarId = avatar?.metadata.avatar_id || '';
  const hasHistory = avatar?.chatHistory?.hasHistory || false;

  const handleToggleGallery = () => {
    setIsGalleryOpen(!isGalleryOpen);
  };

  // Handle back navigation - similar to mobile handleBack
  const handleBack = () => {
    // leave chat first
    if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
      globalLKRoomState.room.disconnect();
      setChatStatus(ChatStatus.INFO);
    }
    
    // navigate back
    if (typeof window !== 'undefined' && window.history.length > 1 &&
        // if the referrer is the same domain, go back (i.e don't go back to google login)
        document.referrer.includes(window.location.hostname)
    ) {
      window.history.back();
    } else {
      // Go to home or characters page
      window.location.href = '/';
    }
  };

  return (
    <>
      <div className="relative w-full lg:w-auto lg:h-full flex-shrink-0 aspect-[9/16] min-w-[500px]" data-unified-chat-panel>
        <div className="flex flex-col w-full h-full overflow-hidden relative bg-black/40 rounded-r-[5px]">

          {/* 1. Profile Header */}
          <ProfileHeader
            onToggleSidePanel={onToggleSidePanel}
            onToggleGallery={handleToggleGallery}
            onBack={handleBack}
          />

          {/* 2. Status Banner */}
          {/* <StatusBanner 
            chatState={chatState}
          /> */}

          {/* 3. Chat Messages Container with Unified Controls */}
          <ChatMessagesContainer
            onLeaveChat={onLeaveChat}
            onStartChat={onStartChat}
            isGalleryOpen={isGalleryOpen}
            onToggleGallery={handleToggleGallery}
          />

        </div>
      </div>
      
      {/* Audio Unlocker - always present when room exists */}
      <AudioUnlocker />
    </>
  );
} 
